import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, retry } from "rxjs";
import { environment } from "src/environments/environment";
import { SociedadViewModel } from "../models/centros/SociedadViewModel";
import { ActividadViewModel } from "../models/centros/actividad.model";
import {
	AdquisicionReactivosViewModel,
	ConsolidacionFinancieraViewModel,
	ContratoElectricidadViewModel,
	PagoCombustibleVehiculosViewModel,
	PagoCombustibleViewModel,
	PagoElectricidadViewModel,
	TipoPagoElecOMViewModel,
	TitularFangosViewModel,
	TitularResiduosDesbasteViewModel,
	TitularResiduosNoPeligrososViewModel,
	TitularResiduosPeligrososViewModel,
	TitularidadEnergiaViewModel,
	TipoJointVentureViewModel,
	UbicacionEspanaViewModel,
} from "../models/centros/cebe-contract-list-base.model";
import { TipoRiesgoSocialViewModel } from "../models/centros/cebe-pal.model";
import {
	CebesCentrosViewModel,
	CentroActividadViewModel,
	DesnitrificacionViewModel,
	DigestionAnaerobiaFangoViewModel,
	LocalizacionViewModel,
	PrimaIncluidaViewModel,
	RecuperacionFosforoViewModel,
	TipoAlcanceViewModel,
	TipoInstalacionViewModel,
	TipoUsoAguaViewModel,
	UsuarioCentroProcedureViewModel,
} from "../models/centros/centro-list-base.model";
import { EmpleadoViewModel } from "../models/centros/empleado.model";
import { PaisViewModel } from "../models/centros/pais.model";
import { TipoProyectoViewModel } from "../models/centros/tipo-proyecto.model";
import { ZonaViewModel } from "../models/centros/zona.model";
import { SuperCentroViewModel } from "../models/centros/super-centro.model";
import { ApiEnum, HttpDataService } from "./http-data.service";

// Models

@Injectable({
	providedIn: "root",
})
export class MasterService {
	constructor(private readonly httpDataService: HttpDataService) {}

	getEmpleados(): Observable<EmpleadoViewModel[]> {
		return this.httpDataService
			.get$<
				EmpleadoViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getEmpleadosList)
			.pipe(
				map((empleados) => {
					return empleados;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<EmpleadoViewModel[]>(
						"getEmpleados",
					),
				),
			);
	}

	getActividades(): Observable<ActividadViewModel[]> {
		return this.httpDataService
			.get$<
				ActividadViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getActividadList)
			.pipe(
				map((actividades) => {
					return actividades;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ActividadViewModel[]>(
						"getActividades",
					),
				),
			);
	}

	getPaises(): Observable<PaisViewModel[]> {
		return this.httpDataService
			.get$<
				PaisViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getPaisList)
			.pipe(
				map((paises) => {
					return paises;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<PaisViewModel[]>(
						"getPaises",
					),
				),
			);
	}

	getZonas(sociedadId: number): Observable<ZonaViewModel[]> {
		return this.httpDataService
			.get$<
				ZonaViewModel[]
			>(ApiEnum.API_ROOT, `${environment.endpoint.master.getZonaList}/${sociedadId}`)
			.pipe(
				map((zonas) => {
					return zonas;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<ZonaViewModel[]>(
						"getZonas",
					),
				),
			);
	}

	getZonasUnidadesOrganicas(
		sociedadId: number,
		responsableID: number | null | undefined,
	): Observable<ZonaViewModel[]> {
		if (responsableID == null) {
			// Si responsableID es null o undefined, devuelve una lista vacía
			return of([]);
		} else {
			return this.httpDataService
				.get$<
					ZonaViewModel[]
				>(ApiEnum.API_ROOT, `${environment.endpoint.master.getZonaListZonaBySociedadByResponsable}/${sociedadId}/${responsableID}`)
				.pipe(
					map((zonas) => {
						return zonas;
					}),
					retry(3),
					catchError(
						this.httpDataService.handleError<ZonaViewModel[]>(
							"getZonasUnidadesOrganicas",
						),
					),
				);
		}
	}

	getTiposProyecto(): Observable<TipoProyectoViewModel[]> {
		return this.httpDataService
			.get$<
				TipoProyectoViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoProyectoList)
			.pipe(
				map((tiposProyecto) => {
					return tiposProyecto;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<TipoProyectoViewModel[]>(
						"getTiposProyecto",
					),
				),
			);
	}

	getSuperCentros(): Observable<SuperCentroViewModel[]> {
		return this.httpDataService
			.get$<SuperCentroViewModel[]>(
				ApiEnum.API_ROOT,
				environment.endpoint.master.getSuperCentroList,
			)
			.pipe(
				map((superCentros) => {
					return superCentros;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<SuperCentroViewModel[]>(
						"getSuperCentros",
					),
				),
			);
	}

	getSociedades(): Observable<SociedadViewModel[]> {
		return this.httpDataService
			.get$<
				SociedadViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getSociedadList)
			.pipe(
				map((sociedades) => {
					return sociedades;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<SociedadViewModel[]>(
						"getSociedades",
					),
				),
			);
	}

	getSociedadById(id: number): Observable<SociedadViewModel> {
		return this.httpDataService
			.get$<SociedadViewModel>(
				ApiEnum.API_ROOT,
				`${environment.endpoint.master.getSociedadById}/${id}`,
			)
			.pipe(
				map((zonas) => {
					return zonas;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<SociedadViewModel>(
						"getSociedadById",
					),
				),
			);
	}

	getConsolidacionFinancieraList(): Observable<
		ConsolidacionFinancieraViewModel[]
	> {
		return this.httpDataService
			.get$<
				ConsolidacionFinancieraViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getConsolidacionFinancieraList)
			.pipe(
				map((consolidacionFinanciera) => {
					return consolidacionFinanciera;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						ConsolidacionFinancieraViewModel[]
					>("getConsolidacionFinancieraList"),
				),
			);
	}

	getAdquisicionReactivosList(): Observable<AdquisicionReactivosViewModel[]> {
		return this.httpDataService
			.get$<
				AdquisicionReactivosViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getAdquisicionReactivosList)
			.pipe(
				map((adquisicionReactivos) => {
					return adquisicionReactivos;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						AdquisicionReactivosViewModel[]
					>("getAdquisicionReactivosList"),
				),
			);
	}

	getContratoElectricidadList(): Observable<ContratoElectricidadViewModel[]> {
		return this.httpDataService
			.get$<
				ContratoElectricidadViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getContratoElectricidadList)
			.pipe(
				map((contratoElectricidad) => {
					return contratoElectricidad;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						ContratoElectricidadViewModel[]
					>("getContratoElectricidadList"),
				),
			);
	}

	getPagoCombustibleList(): Observable<PagoCombustibleViewModel[]> {
		return this.httpDataService
			.get$<
				PagoCombustibleViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getPagoCombustibleList)
			.pipe(
				map((pagoCombustible) => {
					return pagoCombustible;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						PagoCombustibleViewModel[]
					>("getPagoCombustibleList"),
				),
			);
	}

	getPagoCombustibleVehiculosList(): Observable<
		PagoCombustibleVehiculosViewModel[]
	> {
		return this.httpDataService
			.get$<
				PagoCombustibleVehiculosViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getPagoCombustibleVehiculosList)
			.pipe(
				map((pagoCombustibleVehiculos) => {
					return pagoCombustibleVehiculos;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						PagoCombustibleVehiculosViewModel[]
					>("getPagoCombustibleVehiculosList"),
				),
			);
	}

	getPagoElectricidadList(): Observable<PagoElectricidadViewModel[]> {
		return this.httpDataService
			.get$<
				PagoElectricidadViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getPagoElectricidadList)
			.pipe(
				map((pagoElectricidad) => {
					return pagoElectricidad;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						PagoElectricidadViewModel[]
					>("getPagoElectricidadList"),
				),
			);
	}

	getTitularFangosList(): Observable<TitularFangosViewModel[]> {
		return this.httpDataService
			.get$<
				TitularFangosViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTitularFangosList)
			.pipe(
				map((titularFangos) => {
					return titularFangos;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<TitularFangosViewModel[]>(
						"getTitularFangosList",
					),
				),
			);
	}

	getTitularidadEnergiaList(): Observable<TitularidadEnergiaViewModel[]> {
		return this.httpDataService
			.get$<
				TitularidadEnergiaViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTitularidadEnergiaList)
			.pipe(
				map((titularidadEnergia) => {
					return titularidadEnergia;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TitularidadEnergiaViewModel[]
					>("getTitularidadEnergiaList"),
				),
			);
	}

	getTipoJointVentureList(): Observable<TipoJointVentureViewModel[]> {
		return this.httpDataService
			.get$<
				TipoJointVentureViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoJointVentureList)
			.pipe(
				map((tipoJointVenture) => {
					return tipoJointVenture;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TipoJointVentureViewModel[]
					>("getTipoJointVentureList"),
				),
			);
	}

	getTitularResiduosDesbasteList(): Observable<
		TitularResiduosDesbasteViewModel[]
	> {
		return this.httpDataService
			.get$<
				TitularResiduosDesbasteViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTitularResiduosDesbasteList)
			.pipe(
				map((titularResiduosDesbaste) => {
					return titularResiduosDesbaste;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TitularResiduosDesbasteViewModel[]
					>("getTitularResiduosDesbasteList"),
				),
			);
	}

	getTitularResiduosNoPeligrososList(): Observable<
		TitularResiduosNoPeligrososViewModel[]
	> {
		return this.httpDataService
			.get$<
				TitularResiduosNoPeligrososViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTitularResiduosNoPeligrososList)
			.pipe(
				map((titularResiduosNoPeligrosos) => {
					return titularResiduosNoPeligrosos;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TitularResiduosNoPeligrososViewModel[]
					>("getTitularResiduosNoPeligrososList"),
				),
			);
	}

	getTitularResiduosPeligrososList(): Observable<
		TitularResiduosPeligrososViewModel[]
	> {
		return this.httpDataService
			.get$<
				TitularResiduosPeligrososViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTitularResiduosPeligrososList)
			.pipe(
				map((titularResiduosPeligrosos) => {
					return titularResiduosPeligrosos;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TitularResiduosPeligrososViewModel[]
					>("getTitularResiduosPeligrososList"),
				),
			);
	}

	getUbicacionEspanaList(): Observable<UbicacionEspanaViewModel[]> {
		return this.httpDataService
			.get$<
				UbicacionEspanaViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getUbicacionEspanaList)
			.pipe(
				map((ubicacionEspana) => {
					return ubicacionEspana;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						UbicacionEspanaViewModel[]
					>("getUbicacionEspanaList"),
				),
			);
	}

	getTipoPagoElecOMList(): Observable<TipoPagoElecOMViewModel[]> {
		return this.httpDataService
			.get$<
				TipoPagoElecOMViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoPagoElecOMList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<TipoPagoElecOMViewModel[]>(
						"getTipoPagoElecOMList",
					),
				),
			);
	}

	getTipoInstalacionList(): Observable<TipoInstalacionViewModel[]> {
		return this.httpDataService
			.get$<
				TipoInstalacionViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoInstalacionList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TipoInstalacionViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getCentroActividadList(): Observable<CentroActividadViewModel[]> {
		return this.httpDataService
			.get$<
				CentroActividadViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getCentroActividadList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						CentroActividadViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getLocalizacionList(): Observable<LocalizacionViewModel[]> {
		return this.httpDataService
			.get$<
				LocalizacionViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getLocalizacionList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<LocalizacionViewModel[]>(
						"getTipoPagoElecOMList",
					),
				),
			);
	}

	getDesnitrificacionList(): Observable<DesnitrificacionViewModel[]> {
		return this.httpDataService
			.get$<
				DesnitrificacionViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getDesnitrificacionList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						DesnitrificacionViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getDigestionAnaerobiaFangoList(): Observable<
		DigestionAnaerobiaFangoViewModel[]
	> {
		return this.httpDataService
			.get$<
				DigestionAnaerobiaFangoViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getDigestionAnaerobiaFangoList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						DigestionAnaerobiaFangoViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getCebesCentrosList(): Observable<CebesCentrosViewModel[]> {
		return this.httpDataService
			.get$<
				CebesCentrosViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getCebesCentrosList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<CebesCentrosViewModel[]>(
						"getTipoPagoElecOMList",
					),
				),
			);
	}

	getPrimaIncluidaList(): Observable<PrimaIncluidaViewModel[]> {
		return this.httpDataService
			.get$<
				PrimaIncluidaViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getPrimaIncluidaList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<PrimaIncluidaViewModel[]>(
						"getTipoPagoElecOMList",
					),
				),
			);
	}

	getTipoAlcanceList(): Observable<TipoAlcanceViewModel[]> {
		return this.httpDataService
			.get$<
				TipoAlcanceViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoAlcanceList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<TipoAlcanceViewModel[]>(
						"getTipoPagoElecOMList",
					),
				),
			);
	}

	getRecuperacionFosforoList(): Observable<RecuperacionFosforoViewModel[]> {
		return this.httpDataService
			.get$<
				RecuperacionFosforoViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getRecuperacionFosforoList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						RecuperacionFosforoViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getUsuarioCentroProcedureList(): Observable<
		UsuarioCentroProcedureViewModel[]
	> {
		return this.httpDataService
			.get$<
				UsuarioCentroProcedureViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getUsuarioCentroProcedureList)
			.pipe(
				map((tipoPagoElecOM) => {
					return tipoPagoElecOM;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						UsuarioCentroProcedureViewModel[]
					>("getTipoPagoElecOMList"),
				),
			);
	}

	getTipoRiesgoSocialList(): Observable<TipoRiesgoSocialViewModel[]> {
		return this.httpDataService
			.get$<
				TipoRiesgoSocialViewModel[]
			>(ApiEnum.API_ROOT, environment.endpoint.master.getTipoRiesgoSocialList)
			.pipe(
				map((tipoRiesgoSocial) => {
					return tipoRiesgoSocial;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<
						TipoRiesgoSocialViewModel[]
					>("getTipoRiesgoSocialList"),
				),
			);
	}

	getUsoAgua(
		aguaProducto: boolean,
		aguaRegenerada: boolean,
	): Observable<TipoUsoAguaViewModel[]> {
		let queryParams = new HttpParams();

		if (aguaProducto == true) {
			queryParams = queryParams.append("producto", aguaProducto);
		}

		if (aguaRegenerada == true) {
			queryParams = queryParams.append("regenerada", aguaRegenerada);
		}

		return this.httpDataService
			.get$<
				TipoUsoAguaViewModel[]
			>(ApiEnum.API_ROOT, `${environment.endpoint.master.getListTipoUsoAgua}?${queryParams}`)
			.pipe(
				map((tipoUsoAgua) => {
					return tipoUsoAgua;
				}),
				retry(3),
				catchError(
					this.httpDataService.handleError<TipoUsoAguaViewModel[]>(
						"getListTipoUsoAgua",
					),
				),
			);
	}
}
